import { GetObjectCommand, PutObjectCommand } from '@aws-sdk/client-s3';
import { s3 } from './awsS3Storage';

export interface Job {
  id: string;
  title: string;
  location: string;
  employment_type: 'full_time' | 'part_time' | 'contract';
  description: string;
  requirements: string;
  salary_min?: number;
  salary_max?: number;
  created_at: string;
  updated_at: string;
  published: boolean;
  lang: 'de' | 'bs' | 'en';
}

const S3_BUCKET = import.meta.env.VITE_AWS_S3_BUCKET;
const JOBS_KEY = 'inquiries/jobs/jobs.json';
const LOCAL_JOBS_KEY = 'viaMedicaJobs';

const sortByCreatedAtDesc = (jobs: Job[]): Job[] =>
  [...jobs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

const readLocalJobs = (): Job[] => {
  try {
    const raw = localStorage.getItem(LOCAL_JOBS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLocalJobs = (jobs: Job[]) => {
  localStorage.setItem(LOCAL_JOBS_KEY, JSON.stringify(jobs));
};

const readJobsFromS3 = async (): Promise<Job[]> => {
  if (!S3_BUCKET) {
    return readLocalJobs();
  }

  try {
    const response = await s3.send(
      new GetObjectCommand({
        Bucket: S3_BUCKET,
        Key: JOBS_KEY,
      })
    );

    const text = await response.Body?.transformToString();
    if (!text) return [];

    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // If file does not exist or AWS is unavailable, fall back to local storage.
    return readLocalJobs();
  }
};

const writeJobsToS3 = async (jobs: Job[]): Promise<boolean> => {
  if (!S3_BUCKET) {
    writeLocalJobs(jobs);
    return true;
  }

  try {
    await s3.send(
      new PutObjectCommand({
        Bucket: S3_BUCKET,
        Key: JOBS_KEY,
        Body: JSON.stringify(jobs),
        ContentType: 'application/json',
      })
    );

    // Keep local copy as cache/fallback.
    writeLocalJobs(jobs);
    return true;
  } catch {
    // AWS failed, still persist locally so admin can continue working.
    writeLocalJobs(jobs);
    return true;
  }
};

export const fetchPublishedJobs = async (lang?: string): Promise<Job[]> => {
  const jobs = await readJobsFromS3();
  const filtered = jobs.filter((job) => job.published && (!lang || job.lang === lang));
  return sortByCreatedAtDesc(filtered);
};

export const fetchAllJobs = async (): Promise<Job[]> => {
  const jobs = await readJobsFromS3();
  return sortByCreatedAtDesc(jobs);
};

export const createJob = async (
  job: Omit<Job, 'id' | 'created_at' | 'updated_at'>
): Promise<Job | null> => {
  const now = new Date().toISOString();
  const newJob: Job = {
    ...job,
    id: crypto.randomUUID(),
    created_at: now,
    updated_at: now,
  };

  const existing = await readJobsFromS3();
  const updated = [newJob, ...existing];
  const ok = await writeJobsToS3(updated);
  return ok ? newJob : null;
};

export const updateJob = async (id: string, updates: Partial<Job>): Promise<Job | null> => {
  const existing = await readJobsFromS3();
  const index = existing.findIndex((job) => job.id === id);
  if (index === -1) return null;

  const updatedJob: Job = {
    ...existing[index],
    ...updates,
    id,
    updated_at: new Date().toISOString(),
  };

  const updatedJobs = [...existing];
  updatedJobs[index] = updatedJob;

  const ok = await writeJobsToS3(updatedJobs);
  return ok ? updatedJob : null;
};

export const deleteJob = async (id: string): Promise<boolean> => {
  const existing = await readJobsFromS3();
  const updated = existing.filter((job) => job.id !== id);

  if (updated.length === existing.length) {
    return false;
  }

  return writeJobsToS3(updated);
};
